import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, MemberBooking, WorkoutPlan } from '../api';
import AgendaRow, { AgendaKind } from '../components/AgendaRow';
import type { IconName } from '../design/Icon';
import { fmtDateLong, todayStr, addDaysStr } from '../design/atoms';
import type { MemberGym } from '../hooks/useMemberGyms';

/* Unified calendar: one personal wellness feed of planned workouts and studio
   bookings across every gym, merged by day and anchored on today. Pure vertical
   scroll — scroll up for the past, down for what's coming. No month grid and no
   horizontal date switching (the mobile app's calendar tab, ported). */

interface OutletCtx {
  gyms: MemberGym[];
  loading: boolean;
}

/** A booking annotated with the gym it belongs to (this view is cross-gym). */
interface CalBooking extends MemberBooking {
  gymName: string;
}

interface FeedRow {
  key: string;
  kind: AgendaKind;
  time: string | null;
  title: string;
  sub?: string;
  icon?: IconName;
  to: string;
}

interface FeedDay {
  date: string;
  rows: FeedRow[];
}

/** How far back the feed reaches — matches the mobile app's window. */
const PAST_WINDOW_DAYS = 30;
/** Minimum look-ahead when the member has no plan and nothing booked. */
const MIN_FUTURE_DAYS = 7;

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function MemberCalendarPage() {
  const { token } = useMemberAuth();
  const { gyms, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();

  const today = todayStr();
  const [plan, setPlan] = useState<WorkoutPlan | null>(null);
  const [bookings, setBookings] = useState<CalBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const from = useMemo(() => addDaysStr(today, -PAST_WINDOW_DAYS), [today]);
  const to = useMemo(() => addDaysStr(today, 90), [today]);

  const load = useCallback(() => {
    if (!token || gymsLoading) return;
    setLoading(true);
    setError(null);

    const bookingGyms = gyms.filter(g => g.features?.class_scheduling);
    Promise.allSettled([
      mfetch(token, '/member/plan/active'),
      ...bookingGyms.map(g =>
        mfetch(token, '/member/bookings', { params: { gymId: g.id, from, to } })
          .then(r => ({ gym: g, rows: (r.data ?? []) as MemberBooking[] }))
      ),
    ])
      .then(results => {
        const [planResult, ...bookingResults] = results;
        if (planResult.status === 'fulfilled') {
          setPlan(((planResult.value as any)?.data ?? null) as WorkoutPlan | null);
        }
        const merged: CalBooking[] = [];
        for (const r of bookingResults) {
          if (r.status !== 'fulfilled') continue;
          const { gym, rows } = r.value as { gym: MemberGym; rows: MemberBooking[] };
          for (const b of rows) merged.push({ ...b, gymName: gym.displayName || gym.name });
        }
        setBookings(merged);
        // Only a total failure is worth an error card — one gym failing still
        // leaves a usable calendar.
        if (results.every(r => r.status === 'rejected')) {
          const first = results[0];
          setError(first.status === 'rejected' && first.reason instanceof MemberApiError
            ? first.reason.message
            : "Couldn't load your calendar");
        }
      })
      .finally(() => setLoading(false));
  }, [token, gymsLoading, gyms, from, to]);

  useEffect(load, [load]);

  const multiGym = gyms.length > 1;

  const feed = useMemo<FeedDay[]>(() => {
    const byDate = new Map<string, FeedRow[]>();
    const push = (date: string, row: FeedRow) => {
      if (date < from || date > to) return;
      if (!byDate.has(date)) byDate.set(date, []);
      byDate.get(date)!.push(row);
    };

    for (const b of bookings) {
      const s = b.session;
      const sub = [
        s.coach?.name,
        s.room?.name,
        s.branch?.displayName,
        multiGym ? b.gymName : null,
      ].filter(Boolean).join(' · ');
      push(s.date, {
        key: `b-${b.id}`,
        kind: b.status === 'attended' ? 'done' : 'book',
        time: s.startTime,
        title: s.class.name,
        sub: sub || b.gymName,
        to: '/app/bookings',
      });
    }

    for (const d of plan?.days ?? []) {
      const logged = d.items.filter(i => i.status === 'done').length;
      const allLogged = d.items.length > 0 && logged === d.items.length;
      push(d.date, {
        key: `p-${d.id}`,
        kind: d.isRest ? 'rest' : allLogged ? 'done' : 'plan',
        time: d.startTime,
        title: d.isRest ? 'Rest & mobility' : `${d.focus} day`,
        sub: d.isRest
          ? 'Planned recovery day'
          : logged
            ? `${logged} of ${d.items.length} logged`
            : `${d.items.length} exercise${d.items.length === 1 ? '' : 's'}`,
        to: `/app/session/${d.date}`,
      });
    }

    // The daily log always has a home on today, plan or no plan.
    push(today, {
      key: 'log-today',
      kind: 'log',
      time: null,
      title: 'Daily log',
      sub: 'Weight, metrics & how it felt',
      icon: 'chart',
      to: '/app/progress/log',
    });

    return [...byDate.entries()]
      .map(([date, rows]) => ({
        date,
        rows: rows.sort((a, b) => (a.time ?? '99:99').localeCompare(b.time ?? '99:99')),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [bookings, plan, today, from, to, multiGym]);

  const todayRef = useRef<HTMLDivElement | null>(null);
  const anchored = useRef(false);
  useEffect(() => {
    if (loading || anchored.current || !todayRef.current) return;
    anchored.current = true;
    todayRef.current.scrollIntoView({ block: 'start' });
    window.scrollBy(0, -8);
  }, [loading, feed]);

  const todayRows = feed.find(d => d.date === today)?.rows ?? [];
  const todayHasWork = todayRows.some(r => r.kind !== 'log');
  const horizonEnd = feed.length ? feed[feed.length - 1].date : today;

  return (
    <div className="mpage">
      <div className="between" style={{ paddingTop: 8 }}>
        <div className="dp dp-l">Calendar</div>
        <button className="btn gold sm" onClick={() => navigate('/app/plan/new')}>＋ Plan week</button>
      </div>

      {loading ? (
        <div className="col" style={{ gap: 9 }}>
          <div className="card flat"><div className="sub">Loading your calendar…</div></div>
          {[0, 1, 2].map(i => <div key={i} className="skel" style={{ height: 64 }} />)}
        </div>
      ) : error ? (
        <div className="card flat">
          <div className="mid">Couldn't load your calendar</div>
          <div className="sub wrap" style={{ marginTop: 4 }}>{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      ) : (
        <>
          {feed.map(day => {
            const isToday = day.date === today;
            const past = day.date < today;
            return (
              <div
                key={day.date}
                ref={isToday ? todayRef : undefined}
                className="col"
                style={{ gap: 9, scrollMarginTop: 8 }}
              >
                <div className={isToday ? 'lbl-d' : 'lbl'}>
                  {isToday
                    ? `Today · ${fmtDateLong(day.date)}`
                    : `${DAY_SHORT[new Date(`${day.date}T00:00:00`).getDay()]} · ${fmtDateLong(day.date)}`}
                </div>
                {day.rows.map(row => (
                  <AgendaRow
                    key={row.key}
                    kind={row.kind}
                    time={row.time}
                    title={row.title}
                    sub={row.sub}
                    icon={row.icon}
                    dimmed={past}
                    onClick={() => navigate(row.to)}
                  />
                ))}
                {isToday && !todayHasWork && (
                  <button
                    className="card dashed"
                    style={{ textAlign: 'center', padding: 16 }}
                    onClick={() => navigate(plan ? '/app/book' : '/app/plan/new')}
                  >
                    <span className="sub">＋ Add a workout or booking</span>
                  </button>
                )}
              </div>
            );
          })}

          {/* No plan at all → the planning nudge becomes the primary CTA */}
          {!plan && (
            <div className="card tint" style={{ padding: 14 }}>
              <div className="between">
                <div className="col" style={{ gap: 1, minWidth: 0 }}>
                  <div className="lbl-d">Weekly planning</div>
                  <div className="mid">Set up your next 7 days</div>
                  <div className="tiny">Takes about a minute</div>
                </div>
                <button className="btn gold sm" onClick={() => navigate('/app/plan/new')}>Start</button>
              </div>
            </div>
          )}

          {plan && horizonEnd <= addDaysStr(today, MIN_FUTURE_DAYS) && (
            <div className="tiny" style={{ textAlign: 'center' }}>
              Your plan runs to {fmtDateLong(horizonEnd)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
